import { EllipseCurve, Matrix3, Vector3 } from "three";
import { Config } from "../../Config/Config.js";
import { Consts } from "../../Utils/Consts.js";
import { LinePointToPoint } from "../Primitive/LinePointToPoint.js";
import { CalibrationShapeBase } from "./CalibrationShapeBase.js";
import { CalibrationMode } from "./CalibrationTool.js";
/**
 * Create a circle registering 3 points. Used to calibrate.
 */
export class CalibrationCircle extends CalibrationShapeBase {
    constructor(viewType) {
        super(viewType);
        this.mode = CalibrationMode.circle;
        this.pointsToRegister = 3;
        this._curveComplexity = 50;
        this._ellipseCurve = new EllipseCurve(0, 0, 0, 0, 0, 2 * Math.PI, false, 0);
        this.add(this._ABLine = new LinePointToPoint("ABLine", Config.calibrationTool_colorHex, this.A, this.B));
        this._ABLine.bindProperty("visible", (m) => { return !m[this._isRegisteringClickKey]; }, [this._isRegisteringClickKey]);
    }
    update() {
        const radius = this.A.position.distanceTo(this.C.position);
        this._ellipseCurve.aX = this.C.position.x;
        this._ellipseCurve.aY = this.C.position.y;
        this._ellipseCurve.xRadius = radius;
        this._ellipseCurve.yRadius = radius;
        this._shape.setGeoPoints(this._ellipseCurve.getPoints(this._curveComplexity));
    }
    onRegValidation(points) {
        if (!this.getCircleData(points)) {
            return 'calibrationCircleRadiusError';
        }
        return undefined;
    }
    onRegSuccess(points) {
        const circle = this.getCircleData(points);
        const vecDirRadius = Consts.horDir.clone().setLength(circle.radius);
        this.A.position.copy(circle.center.clone().add(vecDirRadius));
        this.B.position.copy(circle.center.clone().sub(vecDirRadius));
        this.C.position.copy(circle.center);
        this.update();
    }
    onRegMove(point) {
        if (this._registeredPoints.length === 2) {
            const circle = this.getCircleData([...this._registeredPoints, point]);
            if (circle) {
                this._ellipseCurve.aX = circle.center.x;
                this._ellipseCurve.aY = circle.center.y;
                this._ellipseCurve.xRadius = circle.radius;
                this._ellipseCurve.yRadius = circle.radius;
                this._shape.setGeoPoints(this._ellipseCurve.getPoints(this._curveComplexity));
            }
            else {
                this._shape.setGeoPoints([]);
            }
        }
    }
    getCircleData(points) {
        const det = new Matrix3().set(points[0].x, points[0].y, 1, points[1].x, points[1].y, 1, points[2].x, points[2].y, 1).determinant();
        if (Math.abs(det) > this._tolerance) {
            const a = new Matrix3().set(-(points[0].x ** 2 + points[0].y ** 2), points[0].y, 1, -(points[1].x ** 2 + points[1].y ** 2), points[1].y, 1, -(points[2].x ** 2 + points[2].y ** 2), points[2].y, 1).determinant() / det;
            const b = new Matrix3().set(points[0].x, -(points[0].x ** 2 + points[0].y ** 2), 1, points[1].x, -(points[1].x ** 2 + points[1].y ** 2), 1, points[2].x, -(points[2].x ** 2 + points[2].y ** 2), 1).determinant() / det;
            const c = new Matrix3().set(points[0].x, points[0].y, -(points[0].x ** 2 + points[0].y ** 2), points[1].x, points[1].y, -(points[1].x ** 2 + points[1].y ** 2), points[2].x, points[2].y, -(points[2].x ** 2 + points[2].y ** 2)).determinant() / det;
            const radius = Math.sqrt(a ** 2 / 4 + b ** 2 / 4 - c);
            if (radius > Config.calibrationTool_maxCircleRadius) {
                return undefined;
            }
            const center = new Vector3(a / -2, b / -2, 0);
            return { center, radius };
        }
        return undefined;
    }
}
//# sourceMappingURL=CalibrationCircle.js.map